import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useMountEffect } from '@react-hookz/web';
import * as Sentry from '@sentry/react';
import map from 'lodash/map';
import moment from 'moment';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card } from 'src/app/components/Elements/Card';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import SnackBar from 'src/app/components/Elements/SnackBar/container';
import Spinner from 'src/app/components/Elements/Spinner';
import IdVerificationRequired from 'src/app/components/Pages/Checkout/IdVerificationRequired/container';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import type { DeliveryWindow } from 'src/app/types';
import { formatDeliveryWindowTimeRange } from 'src/app/utils/datetime';
import { DeliveryMethodType, SNACK_BAR_TYPES } from 'src/consts';

import { DeliveryWindowItem } from '../components/DeliveryWindowItem';
import { EmptyWindowDay } from '../components/EmptyWindowDay';
import { NoAvailableDeliveryWindowsCard } from '../components/NoAvailableDeliveryWindowsCard';
import { DateGroupContainer } from '../components/styles';

import type { PropsFromRedux } from './container';

const DeliveryWindowSelect: FC<PropsFromRedux> = ({
  checkoutDeliveryWindows,
  activeDeliveryWindowId,
  dispatchUpdateCartAndPushNextCheckoutState,
  tierConsumerMessage,
  cartId,
  firstTimeOrder,
  deliveryWindowFetchError,
  isDeliveryWindowFetching,
  cartUUID,
  dispatchGetOrderHistory,
  accountIsFetching,
  bffCartIsFetching,
  bffCart,
  dispatchLoadCart,
  dispatchLoadAccountAndCartDeliveryWindows,
}) => {
  useSegmentTrack(UNIFIED.viewCheckoutDeliveryWindow);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trackSelectDeliveryWindow] = useLazySegmentTrack(UNIFIED.selectDeliveryWindow);

  useEffect(() => {
    dispatchGetOrderHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  useMountEffect(() => {
    if (!bffCartIsFetching && !bffCart) {
      dispatchLoadCart(cartId);
    }

    if (!isDeliveryWindowFetching) {
      dispatchLoadAccountAndCartDeliveryWindows();
    }
  });

  const handleClick = async (deliveryWindow: DeliveryWindow) => {
    const date = moment(deliveryWindow.startDate).format('MM/DD/YYYY');
    const window = formatDeliveryWindowTimeRange(
      deliveryWindow?.startDate,
      deliveryWindow?.endDate,
      deliveryWindow?.category,
      deliveryWindow?.facilityTimezone
    );

    trackSelectDeliveryWindow({
      selected_date: date,
      cart_id: cartId,
      first_time_order: firstTimeOrder,
      selected_window: window,
    });

    if (!deliveryWindow?.id) {
      Sentry.captureMessage(`No Delivery Window found for cart uuid: ${cartUUID}`);
    }

    /**
     * This will save the selected DeliveryWindow object in
     * Redux, in `state.checkout`.
     *
     * It also asynchronously pushes the user to the next checkout
     * route.
     */
    dispatchUpdateCartAndPushNextCheckoutState({
      type: DeliveryMethodType.courier,
      deliveryWindowId: deliveryWindow?.id,
      cartUUID,
    });
    /**
     * `setIsSubmitting` will only need to be called to set `true` to
     * display a spinner while the user is being pushed to the next route.
     */
    setIsSubmitting(true);
  };

  if (isSubmitting || isDeliveryWindowFetching || accountIsFetching || bffCartIsFetching) {
    return (
      <StandardPage title={'Choose a delivery time'}>
        <Spinner centered />
      </StandardPage>
    );
  }

  return (
    <StandardPage title={'Choose a delivery time'}>
      <div>
        <IdVerificationRequired />
        <div>
          {deliveryWindowFetchError ? (
            <NoAvailableDeliveryWindowsCard />
          ) : (
            map(checkoutDeliveryWindows, ({ day, deliveryWindows }) => {
              return (
                <DateGroupContainer key={day}>
                  <h2 className="label">{day}</h2>
                  <Card>
                    <>
                      {deliveryWindows?.length === 0 ? (
                        <EmptyWindowDay text={'NO AVAILABLE TIMES'} />
                      ) : (
                        deliveryWindows.map((deliveryWindow, index) => {
                          const nextDeliveryWindow = deliveryWindows[index + 1];
                          return (
                            <DeliveryWindowItem
                              key={`window_${deliveryWindow.id}`}
                              deliveryWindow={deliveryWindow}
                              nextWindow={nextDeliveryWindow}
                              activeDeliveryWindowId={activeDeliveryWindowId}
                              handleClick={handleClick}
                            />
                          );
                        })
                      )}
                    </>
                  </Card>
                </DateGroupContainer>
              );
            })
          )}
        </div>
      </div>
      {tierConsumerMessage && (
        <SnackBar
          message={tierConsumerMessage}
          closeButtonLabel="dismiss"
          snackBarType={SNACK_BAR_TYPES.tier_message}
        />
      )}
    </StandardPage>
  );
};

export default DeliveryWindowSelect;
